import { createBrowserRouter } from "react-router-dom";
import App from "./App";
import { AppFeature } from "features/appFeatures/types/AppFeature";
import { MatchType } from "features/appFeatures/types/matchType";
import { liveStreamingSelectors } from "features/liveStreaming/store/selectors";
import { playbackSelectionSelectors } from "features/playback/store/selectors.selection";
import { RouteHandleDocumentTitleExtraInfo } from "types/RouteHandleDocumentTitleExtraInfo";
import { FeatureFlagGuard } from "features/appFeatures/FeatureFlagGuard";

export const createRouter = (drawerWidth: number) =>
    createBrowserRouter([
        {
            path: "/",
            element: <App />,
            hydrateFallbackElement: <p>Loading...</p>,
            children: [
                {
                    path: "/",
                    async lazy() {
                        const { Home } = await import("./features/home/Home");

                        return {
                            element: (
                                <FeatureFlagGuard
                                    accessibleWithFeatureFlags={[
                                        AppFeature.LiveStreaming,
                                        AppFeature.Playback,
                                    ]}
                                    matchType={MatchType.Any}
                                    withAuthentication
                                >
                                    <Home />
                                </FeatureFlagGuard>
                            ),
                        };
                    },
                },
                {
                    handle: {
                        getDocumentTitleExtraInfoSelector: () =>
                            liveStreamingSelectors.selectActiveSiteId,
                    } as RouteHandleDocumentTitleExtraInfo,
                    index: true,
                    path: "livestreaming/:hashedvalue?",
                    async lazy() {
                        const { LiveStreaming } =
                            await import("./features/liveStreaming/LiveStreaming");

                        return {
                            element: (
                                <FeatureFlagGuard
                                    accessibleWithFeatureFlags={[
                                        AppFeature.LiveStreaming,
                                    ]}
                                    withAuthentication
                                >
                                    <LiveStreaming drawerWidth={drawerWidth} />
                                </FeatureFlagGuard>
                            ),
                        };
                    },
                },
                {
                    path: "livestreaming/permalinklanding/site/:siteId/devices/*",
                    async lazy() {
                        const { LandingPageLiveStream } =
                            await import("./features/liveStreaming/landingpage/LandingPageLiveStream");

                        return {
                            element: <LandingPageLiveStream />,
                        };
                    },
                },
                {
                    handle: {
                        getDocumentTitleExtraInfoSelector: () =>
                            playbackSelectionSelectors.selectSiteId,
                    } as RouteHandleDocumentTitleExtraInfo,
                    path: "playback/:siteId?",
                    async lazy() {
                        const { Playback } =
                            await import("./features/playback/Playback");

                        return {
                            element: (
                                <FeatureFlagGuard
                                    accessibleWithFeatureFlags={[
                                        AppFeature.Playback,
                                    ]}
                                    unauthorizedTitle={
                                        "Currently your account doesn't have the required permissions to use the playback functionality."
                                    }
                                    withAuthentication
                                >
                                    <Playback drawerWidth={drawerWidth} />
                                </FeatureFlagGuard>
                            ),
                        };
                    },
                },
                {
                    path: "cases",
                    async lazy() {
                        const { DossiersOverview } =
                            await import("./features/cases/caseManagement/DossiersOverview");

                        return {
                            element: (
                                <FeatureFlagGuard
                                    accessibleWithFeatureFlags={[
                                        AppFeature.CaseManagement,
                                    ]}
                                    unauthorizedTitle={
                                        "Currently your account doesn't have the required permissions to use the playback functionality."
                                    }
                                    withAuthentication
                                >
                                    <DossiersOverview
                                        drawerWidth={drawerWidth}
                                    />
                                </FeatureFlagGuard>
                            ),
                        };
                    },
                },
                {
                    handle: {
                        // getDocumentTitleExtraInfoSelector: () => playbackSelectionSelectors.selectSiteId,
                    } as RouteHandleDocumentTitleExtraInfo,
                    path: "cases/:dossierId",
                    async lazy() {
                        const { Dossier } =
                            await import("./features/cases/caseViewer/Dossier");

                        return {
                            element: (
                                <FeatureFlagGuard
                                    accessibleWithFeatureFlags={[
                                        AppFeature.CaseManagement,
                                    ]}
                                    unauthorizedTitle={
                                        "Currently your account doesn't have the required permissions to use the playback functionality."
                                    }
                                    withAuthentication
                                >
                                    <Dossier drawerWidth={drawerWidth} />
                                </FeatureFlagGuard>
                            ),
                        };
                    },
                },
                {
                    path: "diagnostics",
                    async lazy() {
                        const { Diagnostics } =
                            await import("./features/diagnostics/Diagnostics");

                        return {
                            element: (
                                <FeatureFlagGuard
                                    accessibleWithFeatureFlags={[
                                        AppFeature.Diagnostics,
                                    ]}
                                    unauthorizedTitle={
                                        "Currently your account doesn't have the required permissions to use the diagnostic functionality."
                                    }
                                    withAuthentication
                                >
                                    <Diagnostics drawerWidth={drawerWidth} />
                                </FeatureFlagGuard>
                            ),
                        };
                    },
                },
                {
                    path: "approval/datachangeset/:approvalDataChangeSetId",
                    async lazy() {
                        const { ApprovalDataChangeSetViewer } =
                            await import("./features/approvals/ApprovalDataChangeSetViewer");

                        return {
                            element: (
                                <FeatureFlagGuard
                                    accessibleWithFeatureFlags={[
                                        AppFeature.Approval,
                                    ]}
                                    unauthorizedTitle={
                                        "Currently your account doesn't have the required permissions to view approval data change sets."
                                    }
                                    withAuthentication
                                >
                                    <ApprovalDataChangeSetViewer
                                        drawerWidth={drawerWidth}
                                    />
                                </FeatureFlagGuard>
                            ),
                        };
                    },
                },
            ],
        },
    ]);
